/**
 * SubordinateChainTree — bir personelin alt zincirini recursive tree olarak render eder.
 *
 * `OrganizasyonAgaciPanel.renderNode()` pattern'inin reusable, okuma-only,
 * modal-friendly versiyonu. OrganizasyonAgaciPanel'e dokunulmadı — bu
 * component bağımsız çalışır (ROLE_VISUAL kendi içinde duplicate).
 *
 * Backend: GET /api/organizasyon/chains/child/{personnelId}?include_self=true
 *   → ChainOut: { chain: [{personnel_id, name, role_slug, depth, parent_personnel_id}], count }
 *
 * Props:
 *   personnelId   — kök personel UUID'si
 *   cachedChain   — parent'tan prefetched chain array (varsa fetch atlanır).
 *                   Single-open modal pattern'inde aynı personeli açıp kapatıp
 *                   tekrar açma sırasında parasit fetch'i önler.
 *   onChainLoaded — fetch tamamlanınca parent'a (personnelId, chain) iletir,
 *                   parent kendi cache'ini günceller.
 *   skipRoot      — true: root (depth=0) atlanır, sadece children render edilir.
 *                   Modal'da çağrı satırı zaten kişiyi gösterdiği için duplicate'i önler.
 *   compact       — true: küçük font + daha az padding (modal-friendly).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ChevronDown, ChevronRight, Loader2,
  Shield, Crown, Briefcase, UserCheck, HardHat, Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import apiClient from "@/api/client";


// Role bazlı icon + renk paleti — OrganizasyonAgaciPanel.jsx ile aynı.
// TODO: Bu constant OrganizasyonAgaciPanel.jsx ile duplicate. İleride
// frontend/src/lib/roleVisuals.js'e çıkarılıp her ikisi de import etsin.
// Şimdilik regression riskini önlemek için kopyalandı (Iteration 3 kararı).
const ROLE_VISUAL = {
  admin:            { icon: Shield,    color: "text-red-600",    bg: "bg-red-50" },
  baskan:           { icon: Crown,     color: "text-purple-600", bg: "bg-purple-50" },
  mudur:            { icon: Crown,     color: "text-red-600",    bg: "bg-red-50" },
  mudur_yardimcisi: { icon: Briefcase, color: "text-orange-600", bg: "bg-orange-50" },
  bolge_muhendisi:  { icon: UserCheck, color: "text-blue-600",   bg: "bg-blue-50" },
  formen:           { icon: HardHat,   color: "text-yellow-600", bg: "bg-yellow-50" },
  park_gorevlisi:   { icon: Users,     color: "text-green-600",  bg: "bg-green-50" },
  cim_ekibi:        { icon: Users,     color: "text-green-600",  bg: "bg-green-50" },
};


export default function SubordinateChainTree({
  personnelId,
  cachedChain,
  onChainLoaded,
  skipRoot = false,
  compact = false,
}) {
  const [chain, setChain] = useState(cachedChain || []);
  const [loading, setLoading] = useState(!cachedChain);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => {
    if (cachedChain) {
      return new Set(cachedChain.filter((n) => n.depth <= 1).map((n) => n.personnel_id));
    }
    return new Set();
  });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    // Cache hit: prefetched chain varsa fetch atla.
    if (cachedChain) {
      setChain(cachedChain);
      setLoading(false);
      setError(null);
      setExpanded(new Set(
        cachedChain.filter((n) => n.depth <= 1).map((n) => n.personnel_id)
      ));
      return;
    }
    let mounted = true;
    setLoading(true);
    setError(null);
    apiClient
      .get(`/organizasyon/chains/child/${personnelId}?include_self=true`)
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res.data?.chain) ? res.data.chain : [];
        setChain(list);
        // Otomatik expand: depth=0 (root) ve depth=1 (direkt children)
        // Modal'da bağlamı hızlı görsün diye.
        const autoOpen = new Set(
          list.filter((n) => n.depth <= 1).map((n) => n.personnel_id)
        );
        setExpanded(autoOpen);
        // Parent cache'ini güncelle (varsa)
        onChainLoaded?.(personnelId, list);
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.error?.message || err.message);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
    // retryKey dahil: "Tekrar dene" tıklanınca effect yeniden tetiklenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelId, cachedChain, retryKey]);

  // children'ları parent_personnel_id'ye göre grupla
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const node of chain) {
      const pid = node.parent_personnel_id || "__root__";
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(node);
    }
    // Her grup içinde alfabetik sıralama (TR locale)
    for (const list of map.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name, "tr"));
    }
    return map;
  }, [chain]);

  const roots = useMemo(() => chain.filter((n) => n.depth === 0), [chain]);

  const toggleNode = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const indentPx = compact ? 16 : 20;
  const rowPad = compact ? "py-1 px-1.5" : "py-1.5 px-2";
  const textCls = compact ? "text-xs" : "text-sm";

  const renderNode = (node, depth) => {
    const children = childrenByParent.get(node.personnel_id) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(node.personnel_id);
    const visual = ROLE_VISUAL[node.role_slug] || ROLE_VISUAL.park_gorevlisi;
    const Icon = visual.icon;

    return (
      <div key={node.personnel_id} style={{ marginLeft: `${depth * indentPx}px` }}>
        <div
          className={`flex items-center gap-2 rounded ${rowPad} ${visual.bg} hover:brightness-95`}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggleNode(node.personnel_id)}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              aria-label={isExpanded ? "Daralt" : "Genişlet"}
            >
              {isExpanded
                ? <ChevronDown className="h-3 w-3" />
                : <ChevronRight className="h-3 w-3" />}
            </button>
          ) : (
            <span className="w-3 flex-shrink-0" />
          )}
          <Icon className={`h-3.5 w-3.5 ${visual.color} flex-shrink-0`} />
          <span className={`font-medium ${textCls} text-gray-800 truncate`}>
            {node.name}
          </span>
          <Badge variant="outline" className="text-[10px] font-mono">
            {node.role_slug}
          </Badge>
          {hasChildren && (
            <Badge variant="secondary" className="text-[10px]">
              {children.length} alt
            </Badge>
          )}
        </div>
        {isExpanded && hasChildren && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-2 px-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        Hiyerarşi yükleniyor...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-red-600 py-2 px-1 flex items-center gap-2">
        <span>Hiyerarşi yüklenemedi: {error}</span>
        <button
          type="button"
          onClick={() => setRetryKey((k) => k + 1)}
          className="underline text-red-700 hover:text-red-900"
        >
          Tekrar dene
        </button>
      </div>
    );
  }

  // skipRoot: root yerine direkt children'dan başla
  if (skipRoot) {
    const rootIds = roots.map((r) => r.personnel_id);
    const directChildren = rootIds.flatMap(
      (rid) => childrenByParent.get(rid) || []
    );
    if (directChildren.length === 0) {
      return (
        <div className="text-xs text-gray-500 italic py-2 px-1">
          Alt personel yok.
        </div>
      );
    }
    return (
      <div className="space-y-0.5 py-1">
        {directChildren.map((c) => renderNode(c, 0))}
      </div>
    );
  }

  // skipRoot=false: root'tan başlayarak normal render
  if (roots.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic py-2 px-1">
        Hiyerarşi bilgisi yok.
      </div>
    );
  }
  return (
    <div className="space-y-0.5 py-1">
      {roots.map((r) => renderNode(r, 0))}
    </div>
  );
}
